import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { appPaths } from "../../shared/routing/appPaths";
import ErrorNotice from "../../errors/ErrorNotice";
import { useProjects } from "../../projects/projectContext";
import { useAuthoringOptions, type AuthoringOptions } from "./draft/authoringOptions";
import LoadStatus from "../../ui/feedback/LoadStatus";
import { useTabTitle } from "../../shell/workspace";

interface AuthoringPageProps {
  title: string;
  intro: string;
  /** The form, once the project's items and the step catalogue are read. */
  children: (projectId: string, options: AuthoringOptions) => ReactNode;
}

/** The frame of every authoring form, a detour that the one Back leaves (F87, W-11): title, what the item is, then the form. */
export default function AuthoringPage({ title, intro, children }: AuthoringPageProps) {
  useTabTitle({ kind: "other", name: title });
  const { t } = useTranslation();
  const projectId = useProjects().selectedProjectId;
  return (
    <div className="mx-auto max-w-7xl space-y-4 py-4">
      <header className="space-y-1">
        <h2 className="app-title text-xl font-bold">{title}</h2>
        <p className="app-text-muted max-w-3xl text-sm">{intro}</p>
      </header>
      {projectId === null ? (
        <Link to={appPaths.projects()} className="app-link text-sm underline">{t("projects.needed.title")}</Link>
      ) : (
        <WithOptions projectId={projectId}>{children}</WithOptions>
      )}
    </div>
  );
}

function WithOptions({ projectId, children }: { projectId: string; children: AuthoringPageProps["children"] }) {
  const { t } = useTranslation();
  const { state, reload } = useAuthoringOptions(projectId);
  if (state.status !== "loaded") return <LoadStatus state={state} loadingLabel={t("authoring.loadingOptions")} loadingClassName="py-10" onRetry={reload} />;
  return (
    <>
      {state.value.catalogue.status === "failed" ? (
        <div className="space-y-1">
          <p className="app-help-text text-sm">{t("authoring.catalogueMissing")}</p>
          <ErrorNotice error={state.value.catalogue.error} compact={true} />
        </div>
      ) : null}
      {children(projectId, state.value)}
    </>
  );
}
